import React, { useState, useCallback, useEffect } from 'react';
import { useAdmin } from 'src/admin';
import { IconContact, IconAlert, IconCheck, IconClose } from 'src/icons';
import { addAdminByEmailOrPhoneApi, putAdminListApi, getAdminUsersApi } from 'src/services/api';

const AdminAdmins: React.FC = () => {
  const { isSuperAdmin, adminUserIds, superAdminUserId, refreshAdminData } = useAdmin();
  const [addByEmail, setAddByEmail] = useState('');
  const [addByPhone, setAddByPhone] = useState('');
  const [newUserId, setNewUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'email' | 'id'>('email');
  const [adminUserDetails, setAdminUserDetails] = useState<Record<number, { email: string | null; phone: string | null }>>({});

  const user = JSON.parse(localStorage.getItem('flower_power_notes_user') || '{}');
  const authToken = user?.AuthToken;

  const loadAdminUserDetails = useCallback(async () => {
    if (!authToken || adminUserIds.length === 0) {
      setAdminUserDetails({});
      return;
    }
    const result = await getAdminUsersApi(authToken);
    if (result.error || !result.users) {
      setAdminUserDetails({});
      return;
    }
    const details: Record<number, { email: string | null; phone: string | null }> = {};
    const adminIdSet = new Set(adminUserIds);
    result.users.forEach((u) => {
      if (adminIdSet.has(u.id)) {
        details[u.id] = { email: u.email ?? null, phone: u.phone ?? null };
      }
    });
    setAdminUserDetails(details);
  }, [authToken, adminUserIds]);

  useEffect(() => {
    if (isSuperAdmin) loadAdminUserDetails();
  }, [isSuperAdmin, loadAdminUserDetails]);

  const getAdminDisplayLabel = (userId: number): string => {
    const d = adminUserDetails[userId];
    if (d) {
      if (d.email && d.email.trim()) return d.email.trim();
      if (d.phone && d.phone.trim()) return d.phone.trim();
    }
    return `User #${userId}`;
  };

  const getAdminSecondaryLabel = (userId: number): string | null => {
    const d = adminUserDetails[userId];
    if (!d) return null;
    const email = d.email?.trim();
    const phone = d.phone?.trim();
    if (email && phone) return phone;
    if (email || phone) return null;
    return null;
  };

  const handleAddByEmailOrPhone = async () => {
    const email = addByEmail.trim();
    const phone = addByPhone.trim();
    if (!email && !phone) {
      setError('Please enter an email or phone number');
      return;
    }
    setError(null);
    setSaving(true);
    const result = await addAdminByEmailOrPhoneApi(authToken, email ? { email } : { phone });
    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setAddByEmail('');
    setAddByPhone('');
    setMessage(`✅ Admin permissions granted to ${email || phone}`);
    await refreshAdminData();
    await loadAdminUserDetails();
    setTimeout(() => setMessage(null), 4000);
  };

  const handleAddByUserId = async () => {
    const id = parseInt(newUserId.trim(), 10);
    if (Number.isNaN(id) || id < 1) {
      setError('Please enter a valid user ID (positive number)');
      return;
    }
    if (adminUserIds.includes(id)) {
      setError('This user is already an admin');
      return;
    }
    if (id === superAdminUserId) {
      setError('Cannot add super admin – they already have admin access');
      return;
    }
    setError(null);
    setSaving(true);
    const next = [...adminUserIds, id].sort((a, b) => a - b);
    const result = await putAdminListApi(authToken, next);
    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setNewUserId('');
    setMessage(`✅ Admin permissions granted to User #${id}`);
    await refreshAdminData();
    await loadAdminUserDetails();
    setTimeout(() => setMessage(null), 4000);
  };

  const handleRemove = async (userId: number) => {
    const label = getAdminDisplayLabel(userId);
    if (!window.confirm(`Are you sure you want to remove admin permissions from ${label}?`)) return;
    setError(null);
    setSaving(true);
    const next = adminUserIds.filter((id) => id !== userId);
    const result = await putAdminListApi(authToken, next);
    setSaving(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setMessage(`✅ Admin permissions removed from ${label}`);
    await refreshAdminData();
    await loadAdminUserDetails();
    setTimeout(() => setMessage(null), 4000);
  };

  const regularAdmins = adminUserIds.filter(id => id !== superAdminUserId);
  const totalAdmins = regularAdmins.length + (superAdminUserId ? 1 : 0);

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col flex-1 p-1.5 min-h-0 overflow-hidden">
        <header className="flex items-center gap-1 mb-1 shrink-0">
          <h1 className="font-semibold text-gray-900 text-sm">Admins</h1>
        </header>
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="bg-white p-2 border border-gray-200 rounded text-center">
            <h2 className="mb-1 font-semibold text-gray-900 text-sm">Access Restricted</h2>
            <p className="mb-2 text-gray-600 text-xs">Only super admin can manage. Super admin is set in API env.</p>
            <div className="mt-2 pt-2 border-gray-200 border-t">
              <h3 className="mb-1 font-semibold text-gray-900 text-sm">Current Admins</h3>
              <div className="space-y-1">
                {superAdminUserId && (
                  <div className="flex justify-between items-center bg-blue-50 p-1.5 border border-blue-100 rounded">
                    <span className="font-medium text-gray-900 text-xs truncate">User #{superAdminUserId} (Super)</span>
                    <span className="text-blue-600 text-xs shrink-0">Permanent</span>
                  </div>
                )}
                {regularAdmins.map((id) => (
                  <div key={id} className="flex justify-between items-center bg-white p-1.5 border border-gray-200 rounded">
                    <span className="font-medium text-gray-900 text-xs truncate">User #{id}</span>
                  </div>
                ))}
                {totalAdmins === 0 && <p className="py-2 text-gray-500 text-xs">No admins</p>}
              </div>
              {totalAdmins > 0 && <p className="mt-1 text-gray-500 text-xs">Total: {totalAdmins}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 max-h-full overflow-hidden">
      <header className="bg-white px-3 py-2 border-gray-200 border-b shrink-0">
        <div className="flex items-center gap-2">
          <div className="flex justify-center items-center bg-gradient-to-br from-blue-500 to-indigo-600 shadow rounded-lg w-7 h-7">
            <IconContact className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="font-semibold text-gray-900 text-sm truncate">Admin Management</h1>
            <p className="text-gray-500 text-xs truncate">Grant or revoke administrator permissions</p>
          </div>
        </div>
      </header>

      <div className="flex flex-col flex-1 px-3 py-2 min-h-0 overflow-hidden">
        {(message || error) && (
          <div
            className={`mb-2 rounded-lg border px-2.5 py-2 text-xs ${
              error
                ? 'border-red-200 bg-gradient-to-r from-red-50/90 to-red-50/70 text-red-800'
                : 'border-emerald-200 bg-gradient-to-r from-emerald-50/90 to-emerald-50/70 text-emerald-800'
            }`}
            role="alert"
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`flex-shrink-0 w-4 h-4 rounded-full ${error ? 'bg-red-100' : 'bg-emerald-100'} flex items-center justify-center`}>
                  {error ? (
                    <IconAlert className="w-2.5 h-2.5 text-red-500" />
                  ) : (
                    <IconCheck className="w-2.5 h-2.5 text-emerald-500" />
                  )}
                </div>
                <span className="font-medium truncate">{error || message}</span>
              </div>
              <button
                type="button"
                onClick={() => { setError(null); setMessage(null); }}
                className="ml-2 p-1 rounded text-gray-400 hover:text-gray-600 shrink-0"
                aria-label="Dismiss"
              >
                <IconClose className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 gap-2 grid lg:grid-cols-2 min-h-0" style={{ gridAutoRows: 'minmax(0, 1fr)' }}>
          <div className="flex flex-col bg-white p-2 border border-gray-200 rounded min-h-0 overflow-hidden">
            <div className="flex items-center gap-1 mb-2 pb-1 border-gray-200 border-b shrink-0">
              <h2 className="font-semibold text-gray-900 text-sm truncate">Add Admin</h2>
            </div>

            <div className="flex-1 min-h-0 overflow-auto">
            <div className="flex gap-0.5 bg-gray-100 mb-2 p-0.5 border border-gray-200 rounded shrink-0">
              <button type="button" onClick={() => setActiveTab('email')} className={`flex-1 py-1 font-medium text-xs rounded transition-colors ${activeTab === 'email' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-white/50'}`}>Email/Phone</button>
              <button type="button" onClick={() => setActiveTab('id')} className={`flex-1 py-1 font-medium text-xs rounded transition-colors ${activeTab === 'id' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-white/50'}`}>User ID</button>
            </div>

            {activeTab === 'email' && (
              <div className="space-y-2">
                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Email</label>
                  <input type="email" value={addByEmail} onChange={(e) => setAddByEmail(e.target.value)} placeholder="user@example.com" className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full text-xs" />
                </div>
                <p className="text-gray-500 text-xs">or</p>
                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">Phone</label>
                  <input type="tel" value={addByPhone} onChange={(e) => setAddByPhone(e.target.value)} placeholder="+1 555 123 4567" className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full text-xs" />
                </div>
                <button type="button" onClick={handleAddByEmailOrPhone} disabled={saving || (!addByEmail.trim() && !addByPhone.trim())} className={`w-full py-1 rounded font-medium text-xs ${saving || (!addByEmail.trim() && !addByPhone.trim()) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>{saving ? '…' : 'Grant Admin'}</button>
              </div>
            )}

            {activeTab === 'id' && (
              <div className="space-y-2">
                <div>
                  <label className="block mb-0.5 font-medium text-gray-700 text-xs">User ID</label>
                  <input type="number" min={1} value={newUserId} onChange={(e) => setNewUserId(e.target.value)} placeholder="ID" className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 w-full text-xs" />
                </div>
                <button type="button" onClick={handleAddByUserId} disabled={saving || !newUserId.trim()} className={`w-full py-1 rounded font-medium text-xs ${saving || !newUserId.trim() ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>{saving ? '…' : 'Add by ID'}</button>
              </div>
            )}
            </div>
          </div>

          <div className="flex flex-col bg-white p-2 border border-gray-200 rounded min-h-0 overflow-hidden">
            <div className="flex justify-between items-center mb-1 pb-1 border-gray-200 border-b shrink-0">
              <h2 className="font-semibold text-gray-900 text-sm truncate">Current ({totalAdmins})</h2>
            </div>

            <div className="flex flex-col flex-1 min-h-0">
              {regularAdmins.length === 0 && !superAdminUserId ? (
                <div className="flex flex-1 justify-center items-center py-2 text-center">
                  <p className="text-gray-500 text-xs">No admins. Add via form.</p>
                </div>
              ) : (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <div className="flex-1 space-y-1 -mr-0.5 pr-0.5 min-h-0 overflow-y-auto">
                    {superAdminUserId && (
                      <div className="flex justify-between items-center bg-blue-50 p-1.5 border border-blue-200 rounded">
                        <span className="font-medium text-gray-900 text-xs truncate" title={getAdminDisplayLabel(superAdminUserId)}>{getAdminDisplayLabel(superAdminUserId)}</span>
                        <span className="text-blue-600 text-xs shrink-0">Super (ENV)</span>
                      </div>
                    )}
                    {regularAdmins.map((id) => (
                      <div key={id} className="flex justify-between items-center bg-white p-1.5 border border-gray-200 hover:border-blue-300 rounded">
                        <span className="font-medium text-gray-900 text-xs truncate" title={getAdminDisplayLabel(id)}>{getAdminDisplayLabel(id)}</span>
                        <button type="button" onClick={() => handleRemove(id)} disabled={saving} className="bg-red-50 hover:bg-red-100 disabled:opacity-50 px-1.5 py-0.5 border border-red-200 rounded font-medium text-red-700 text-xs disabled:cursor-not-allowed">Remove</button>
                      </div>
                    ))}
                  </div>
                  {totalAdmins > 0 && (
                    <div className="mt-1 pt-1 border-gray-200 border-t text-gray-500 text-xs shrink-0">
                      Total: <span className="font-medium text-gray-900">{totalAdmins}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-amber-50/80 mt-1 p-1 border border-amber-200 rounded shrink-0">
          <p className="text-gray-600 text-xs">Super admin is set in API env. Admins get full panel access. User must have account first.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAdmins;
